/**
 * Word accordion — expandable panel showing Strong's definition
 * for a clicked word in the interactive scripture block.
 */

import { useCallback } from 'react';
import type { ScriptureWord } from '@skjv/common';
import { bibleState } from '@/state/bible-state';
import { DefinitionText } from '@components/strongs-panel/definition-text';

interface WordAccordionProps {
  word: ScriptureWord;
}

export function WordAccordion({ word }: WordAccordionProps) {
  const def = word.definition;

  const handleNumberClick = useCallback((num: string) => {
    bibleState.showStrongsDefinition(num, bibleState.currentLocation, {
      englishWord: word.word,
      websterDefinition: word.websterDefinition,
    });
  }, [word.word, word.websterDefinition]);

  return (
    <div className="word-accordion">
      <div className="word-accordion-header">
        <span className="word-accordion-english">{word.word}</span>
        {word.strongsNumbers.map((num) => (
          <button
            key={num}
            className="strongs-chip"
            onClick={() => handleNumberClick(num)}
          >
            {num}
          </button>
        ))}
      </div>

      {def && (
        <div className="word-accordion-body">
          <div className="word-accordion-native">
            <span className="word-accordion-native-text">{def.native}</span>
            <span className="word-accordion-phonetic">{def.phonetic}</span>
            <span className="word-accordion-lang">{def.language}</span>
          </div>
          <p className="word-accordion-definition">
            <DefinitionText
              definition={def.definition}
              language={def.language}
              onLinkClick={handleNumberClick}
            />
          </p>
        </div>
      )}

      {!def && word.strongsNumbers.length > 0 && (
        <p className="word-accordion-hint">
          Click {word.strongsNumbers[0]} to view full definition
        </p>
      )}

      {word.websterDefinition && (
        <div className="webster-section">
          <div className="webster-header">Webster's 1828</div>
          <p className="webster-definition">
            {word.websterDefinition.length > 300
              ? word.websterDefinition.substring(0, 300) + '...'
              : word.websterDefinition}
          </p>
        </div>
      )}
    </div>
  );
}
